import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import { NAV } from "@/constants/testIds";
import { Search, Map, Plus, LogOut, LayoutDashboard, Heart, User, MessageCircle, ShieldCheck, ShieldAlert, Building2 } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { api } from "@/lib/api";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

export const Navbar = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [unread, setUnread] = useState(0);

  useEffect(() => {
    if (!user) {
      setUnread(0);
      return;
    }
    let mounted = true;
    const fetchUnread = async () => {
      try {
        const { data } = await api.get("/messages/unread-count");
        if (mounted) setUnread(data.count);
      } catch {}
    };
    fetchUnread();
    const interval = setInterval(fetchUnread, 30000);
    return () => {
      mounted = false;
      clearInterval(interval);
    };
  }, [user]);

  return (
    <header className="glass sticky top-0 z-50 border-b border-black/5">
      <div className="max-w-7xl mx-auto px-6 lg:px-8 h-16 flex items-center justify-between">
        <Link to="/" data-testid={NAV.brand} className="flex items-center gap-2.5 group">
          <img
            src="/logo-3aqarate.png"
            alt="3aqarate"
            className="h-11 w-auto group-hover:scale-105 transition-transform"
          />
          <div className="hidden sm:flex flex-col leading-none">
            <span className="font-heading font-medium text-[10px] tracking-[0.25em] uppercase text-[#11783A]">{t("nav.tagline")}</span>
            <span className="text-[9px] text-muted-foreground tracking-[0.2em] uppercase mt-0.5">{t("nav.subtitle")}</span>
          </div>
        </Link>

        <nav className="hidden md:flex items-center gap-1 text-sm">
          <Link to="/search" data-testid={NAV.search} className="px-3 py-2 rounded-lg hover:bg-black/5 transition-colors flex items-center gap-1.5">
            <Search className="w-4 h-4" strokeWidth={1.5} /> {t("nav.listings")}
          </Link>
          <Link to="/map" data-testid={NAV.map} className="px-3 py-2 rounded-lg hover:bg-black/5 transition-colors flex items-center gap-1.5">
            <Map className="w-4 h-4" strokeWidth={1.5} /> {t("nav.map")}
          </Link>
          <Link to="/agences" data-testid="nav-agencies" className="px-3 py-2 rounded-lg hover:bg-black/5 transition-colors flex items-center gap-1.5">
            <Building2 className="w-4 h-4" strokeWidth={1.5} /> Agences
          </Link>
          {!user && (
            <Link
              to="/pour-agences"
              data-testid="nav-for-agencies"
              className="px-3 py-2 rounded-lg text-[#D6202C] hover:bg-[#D6202C]/10 transition-colors flex items-center gap-1.5 font-medium"
            >
              Devenir Pro
            </Link>
          )}
          {user && (
            <Link to="/favorites" data-testid={NAV.favorites} className="px-3 py-2 rounded-lg hover:bg-black/5 transition-colors flex items-center gap-1.5">
              <Heart className="w-4 h-4" strokeWidth={1.5} /> {t("nav.favorites")}
            </Link>
          )}
          {user && (
            <Link
              to="/messages"
              data-testid="nav-messages"
              className="px-3 py-2 rounded-lg hover:bg-black/5 transition-colors flex items-center gap-1.5 relative"
            >
              <MessageCircle className="w-4 h-4" strokeWidth={1.5} /> {t("nav.messages")}
              {unread > 0 && (
                <span
                  data-testid="nav-unread-badge"
                  className="absolute -top-1 -right-1 bg-[#D6202C] text-white text-[10px] font-medium w-4 h-4 rounded-full flex items-center justify-center"
                >
                  {unread > 9 ? "9+" : unread}
                </span>
              )}
            </Link>
          )}
        </nav>

        <div className="flex items-center gap-1.5">
          <LanguageSwitcher />
          <Button
            onClick={() => navigate(user ? "/dashboard/new" : "/login")}
            data-testid={NAV.publish}
            className="rounded-full bg-[#D6202C] hover:bg-[#B5181F] text-white font-medium px-5 shadow-sm"
          >
            <Plus className="w-4 h-4 mr-1" strokeWidth={2} />
            {t("nav.publish")}
          </Button>
          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="w-9 h-9 rounded-full bg-[#11783A] text-white flex items-center justify-center font-medium text-sm hover:scale-105 transition-transform">
                  {user.name.charAt(0).toUpperCase()}
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>
                  <div className="flex flex-col">
                    <span className="font-medium">{user.name}</span>
                    <span className="text-xs text-muted-foreground">{user.email}</span>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate("/dashboard")} data-testid={NAV.dashboard}>
                  <LayoutDashboard className="w-4 h-4 mr-2" /> Tableau de bord
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/favorites")}>
                  <Heart className="w-4 h-4 mr-2" /> Favoris
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/messages")}>
                  <MessageCircle className="w-4 h-4 mr-2" /> Messages
                  {unread > 0 && (
                    <span className="ml-auto text-[10px] bg-[#D6202C] text-white px-1.5 py-0.5 rounded-full">{unread}</span>
                  )}
                </DropdownMenuItem>
                {user.role === "admin" && (
                  <DropdownMenuItem onClick={() => navigate("/admin")} data-testid="nav-admin-home">
                    <ShieldCheck className="w-4 h-4 mr-2 text-[#D6202C]" /> Console Admin
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={() => {
                    logout();
                    navigate("/");
                  }}
                  data-testid={NAV.logout}
                >
                  <LogOut className="w-4 h-4 mr-2" /> Déconnexion
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <Button
              variant="ghost"
              onClick={() => navigate("/login")}
              data-testid={NAV.login}
              className="rounded-full font-medium"
            >
              <User className="w-4 h-4 mr-1" strokeWidth={1.5} /> {t("nav.login")}
            </Button>
          )}
        </div>
      </div>
    </header>
  );
};
